/* eslint-disable no-continue */
import get from 'lodash/get';
import noop from 'lodash/noop';

import { SearchQuery } from 'modules/workflows/types';
import { WalkSearchQueryQueryTypes } from './walkSearchQueries.constants';
import { Branch, Level, QueryPath } from './walkSearchQueries.types';
import { getSearchQueryType, hasMaxLevelBeenReached } from './walkSearchQueries.utils';

/** Information return on query callbacks */
interface WalkQueryLevelInfo {
  /** Current query */
  query: SearchQuery;
  level: Level;
  branch: Branch;
  /** The type of query at this level */
  type: WalkSearchQueryQueryTypes;
  /** A string representation of the path to this query */
  path: QueryPath;
  /**
   * Stops the walk function from continuing to walk through each query
   *
   * For performance reasons, is **HIGHLY**  recommended that you run this
   * function when you have the info you need and no longer need to search
   * through the rest of the queries.
   */
  stop: () => void;
}

/** Arguments for the walk query function */
export interface WalkQueriesArgs {
  /** A callback that is run on each query */
  callback?: (opts: WalkQueryLevelInfo) => void;
  /** The max depth to check */
  maxLevel?: Level;
}

interface WalkQueryWalkLevelArgs {
  /** The current level (IE: depth) of the query */
  level?: Level;
  /** A string representation of the path to this level */
  levelPath?: QueryPath;
}

/**
 * Walks through a list of queries and runs a callback on each query.
 *
 * TODO: Add the ability to give each level a name, and to be able use that to
 * determine stopping point instead of a max level.
 */
class SearchQueryWalker {
  /** Has the function stopped running? */
  hasStopped = false;
  /** The max depth to check */
  maxLevel: Level = Infinity;
  /** A callback that is run on each query */
  callback: (opts: WalkQueryLevelInfo) => void = noop;

  constructor({ callback = noop, maxLevel = Infinity }: WalkQueriesArgs = {}) {
    this.callback = callback;
    this.maxLevel = maxLevel;
  }
  /**
   * Internal implementation of the walk function, containing properties that
   * users should not alter directly. (IE: `level`)
   * @private
   *
   * @param {SearchQuery[]} queries List of queries to walk through. Type: {@link SearchQuery}.
   * @param {Level} level The current level (IE: depth) of the query
   */
  private walkLevel(
    searchQueries: SearchQuery[],
    { level = 1, levelPath = '' }: WalkQueryWalkLevelArgs = {},
  ) {
    const maxLevelIsReached = hasMaxLevelBeenReached(level, this.maxLevel);

    for (let branch = 0; branch < searchQueries.length; branch++) {
      // Needs to be called at to stop any concurrently running queries before
      // the callback is called
      if (this.hasStopped) {
        break;
      }

      const query = searchQueries[branch];
      const type = getSearchQueryType(query);
      const stop = this.stop.bind(this);
      const path = `${levelPath}[${branch}]`;

      this.callback.call(this, { query, level, branch, type, path, stop });

      // If `stop()` has been called, stop walking through the queries
      // completely
      if (this.hasStopped) {
        break;
      }

      // If we've reached the max level, do not check the children and move on
      // to the next query
      if (maxLevelIsReached) {
        continue;
      }

      const childQueryPath =
        type === WalkSearchQueryQueryTypes.SearchCombo
          ? 'searchQueries'
          : 'searchCombo.searchQueries';
      const childQueries = get(query, childQueryPath) as SearchQuery[];

      if (childQueries?.length) {
        // Walk through the child queries
        this.walkLevel.call(this, childQueries, {
          level: level + 1,
          levelPath: `${path}.${childQueryPath}`,
        });
      }
    }
  }
  /**
   * Starts walking through the queries
   */
  public walk(queries: SearchQuery[]) {
    this.hasStopped = false;
    this.walkLevel(queries);
  }
  /**
   * Stops walking through the queries.
   *
   * Can either be called on the class instance or on the callback function.
   */
  public stop() {
    this.hasStopped = true;
  }
}

/**
 * Walks through a list of {@link SearchQuery|search queries} and runs a callback for each query.
 *
 * Search queries are nested lists of configurable fields -- they are found in
 * features such as yes/no split, start node, and exit rules.
 *
 * @param {SearchQuery[]} queries List of queries to walk through. Type: {@link SearchQuery}.
 * @param {WalkQueriesArgs} [opts] Optional set of arguments. Type: {@link WalkQueriesArgs}.
 *
 * @see {@link walk} for implementation details.
 *
 * @example
 * walkSearchQueries(queries, {
 *  maxLevel: 2, // stops checking after the second level
 *  callback: ({ query, level, branch, type, path, stop }) => {
 *    // Do something with the query
 *
 *    // To stop iterating through the queries, call `stop()`.
 *    // For performance reasons, it is **HIGHLY** recommended that you run this when you have the info you need.
 *    // EG:
 *    if (query?.dataType === 'customEvent') {
 *      stop();
 *    }
 *  },
 * });
 */
const walkSearchQueries = (queries: SearchQuery[] = [], opts?: WalkQueriesArgs) => {
  // create the walker
  const walker = new SearchQueryWalker(opts);
  // walk through the queries
  walker.walk(queries);

  return walker;
};

export default walkSearchQueries;
